import { useState, useEffect } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import { Button } from "@sakura-grp/ui/button";
import { Calendar, type DateRange } from "@sakura-grp/ui/calendar";
import { Checkbox } from "@sakura-grp/ui/checkbox";
import { Label } from "@sakura-grp/ui/label";
import { useUIStore, type StatusType } from "@/stores/ui";
import { useDirtyDialogGuard } from "@/hooks/use-dirty-dialog-guard";
import { X } from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { ja } from "date-fns/locale";

const PRESETS = [
  { label: "今日", getValue: () => {
    const today = new Date();
    return { from: today, to: today };
  }},
  { label: "過去7日", getValue: () => {
    const today = new Date();
    return { from: subDays(today, 6), to: today };
  }},
  { label: "今月", getValue: () => {
    const today = new Date();
    return { from: startOfMonth(today), to: today };
  }},
  { label: "先月", getValue: () => {
    const today = new Date();
    return { from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)) };
  }},
];

interface FilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FilterDraft {
  dateRange: DateRange | undefined;
  statusTypes: StatusType[];
}

const DEFAULT_STATUS_TYPES: StatusType[] = ["good", "poor"];

const STATUS_OPTIONS: { value: StatusType; label: string; color: string }[] = [
  { value: "good", label: "営業良好", color: "bg-success" },
  { value: "poor", label: "営業不足", color: "bg-red-500" },
];

export function FilterDialog({ open, onOpenChange }: FilterDialogProps) {
  const { filters, setFilters } = useUIStore();
  const [draft, setDraft] = useState<FilterDraft>({
    dateRange: filters.dateRange ?? undefined,
    statusTypes: filters.statusTypes,
  });

  // open 時に store から draft を再同期
  useEffect(() => {
    if (!open) return;
    setDraft({
      dateRange: filters.dateRange ?? undefined,
      statusTypes: filters.statusTypes,
    });
  }, [open, filters.dateRange, filters.statusTypes]);

  // draft と store の差分判定
  const statusKey = (s: StatusType[]) => [...s].sort().join(",");
  const hasChanges =
    draft.dateRange?.from?.getTime() !== filters.dateRange?.from?.getTime() ||
    draft.dateRange?.to?.getTime() !== filters.dateRange?.to?.getTime() ||
    statusKey(draft.statusTypes) !== statusKey(filters.statusTypes);

  const { DirtyConfirmDialog, requestClose } = useDirtyDialogGuard({
    isDirty: hasChanges,
    onClose: () => onOpenChange(false),
  });

  const handleApply = () => {
    setFilters({
      dateRange: draft.dateRange?.from && draft.dateRange?.to
        ? { from: draft.dateRange.from, to: draft.dateRange.to }
        : null,
      statusTypes: draft.statusTypes,
    });
    onOpenChange(false);
  };

  const handleReset = () => {
    setDraft({ dateRange: undefined, statusTypes: DEFAULT_STATUS_TYPES });
  };

  const clearDateRange = () => {
    setDraft((d) => ({ ...d, dateRange: undefined }));
  };

  const handleDateSelect = (range: DateRange | undefined) => {
    setDraft((d) => ({ ...d, dateRange: range }));
  };

  const handlePresetClick = (preset: typeof PRESETS[number]) => {
    const range = preset.getValue();
    setDraft((d) => ({ ...d, dateRange: range }));
  };

  const toggleDraftStatus = (status: StatusType) => {
    setDraft((d) => ({
      ...d,
      statusTypes: d.statusTypes.includes(status)
        ? d.statusTypes.filter((s) => s !== status)
        : [...d.statusTypes, status],
    }));
  };

  const formatDateRange = () => {
    if (!draft.dateRange?.from) return "未選択";
    if (!draft.dateRange.to) return format(draft.dateRange.from, "yyyy/MM/dd", { locale: ja });
    return `${format(draft.dateRange.from, "yyyy/MM/dd", { locale: ja })} ~ ${format(draft.dateRange.to, "yyyy/MM/dd", { locale: ja })}`;
  };

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} dismissible={!hasChanges}>
        <DrawerContent className="flex flex-col p-0">
          {/* Sticky Header */}
          <DrawerHeader className="sticky top-0 bg-background z-10 px-4 pt-4 pb-2 border-b">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-lg font-bold">絞込</DrawerTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="h-8 px-2 text-muted-foreground"
              >
                リセット
              </Button>
            </div>
          </DrawerHeader>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
            {/* 登録日範囲 */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-bold">登録日</Label>
                {draft.dateRange?.from && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDateRange}
                    className="h-8 px-2 text-muted-foreground"
                  >
                    <X className="h-4 w-4 mr-1" />
                    クリア
                  </Button>
                )}
              </div>

              {/* 選択中の日付表示 */}
              <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                {formatDateRange()}
              </div>

              {/* プリセットボタン - タッチフレンドリー */}
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="outline"
                    className="h-11 px-4 text-sm"
                    onClick={() => handlePresetClick(preset)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>

              {/* カレンダー - 直接表示 */}
              <div className="flex justify-center border rounded-lg p-2 bg-background">
                <Calendar
                  mode="range"
                  selected={draft.dateRange}
                  onSelect={handleDateSelect}
                  numberOfMonths={1}
                  locale={ja}
                  className="w-full"
                />
              </div>
            </div>

            {/* 営業ステータス */}
            <div className="space-y-3">
              <Label className="text-base font-bold">営業ステータス</Label>
              <div className="space-y-1">
                {STATUS_OPTIONS.map((status) => (
                  <div
                    key={status.value}
                    className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-muted/50 active:bg-muted cursor-pointer"
                    onClick={() => toggleDraftStatus(status.value)}
                  >
                    <Checkbox
                      id={`status-${status.value}`}
                      checked={draft.statusTypes.includes(status.value)}
                      onCheckedChange={() => toggleDraftStatus(status.value)}
                      className="h-5 w-5"
                    />
                    <Label
                      htmlFor={`status-${status.value}`}
                      className="flex items-center gap-3 cursor-pointer flex-1 text-base"
                    >
                      <span className={`w-4 h-4 rounded-full ${status.color}`} />
                      {status.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sticky Footer */}
          <DrawerFooter className="sticky bottom-0 bg-background border-t px-4 py-4 flex flex-col gap-md">
            <Button
              onClick={handleApply}
              disabled={!hasChanges}
              className="h-12 text-base"
            >
              適用
            </Button>
            <Button
              variant="outline"
              onClick={requestClose}
              className="h-12 text-base"
            >
              やめる
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <DirtyConfirmDialog />
    </>
  );
}
